import { animate } from "motion";
import { type KeyboardEvent, useEffect, useLayoutEffect, useRef } from "react";

import { spring } from "./motion.js";

export type Segment<T extends string> = {
  value: T;
  label: string;
};

type Props<T extends string> = {
  segments: readonly Segment<T>[];
  value: T;
  onChange: (value: T) => void;
  /** aria-label。何を切り替えるコントロールなのかを読み上げに伝える。 */
  label: string;
};

/**
 * iOS のセグメンテッドコントロール相当。
 *
 * 選択インジケータは CSS transition ではなくスプリングで動かす。CSS transition は
 * 途中で掴んで反転させられず、連打すると現在位置ではなく目標値から動き直して飛ぶ。
 * Motion の animate は常に「今の画面上の値」から再ターゲットするので、
 * 収束前にもう一度押しても速度を保ったまま滑らかに行き先だけが変わる。
 *
 * ロールは tablist ではなく radiogroup。切り替えているのは表示するパネルではなく
 * ひとつの設定値で、対応する tabpanel も無いため。radiogroup の作法にあわせて
 * roving tabindex と左右キーでの移動を実装する。
 */
export function SegmentedControl<T extends string>({ segments, value, onChange, label }: Props<T>) {
  const listRef = useRef<HTMLDivElement | null>(null);
  const indicatorRef = useRef<HTMLSpanElement | null>(null);
  // 初回は跳ねさせずに位置だけ合わせる (勢いを伴わない出現でオーバーシュートさせない)。
  const settled = useRef(false);

  const moveIndicator = (animated: boolean) => {
    const list = listRef.current;
    const indicator = indicatorRef.current;
    if (!list || !indicator) return;

    const active = list.querySelector<HTMLElement>(`[data-value="${CSS.escape(value)}"]`);
    if (!active) return;

    const x = active.offsetLeft;
    const width = active.offsetWidth;
    if (width === 0) return;

    if (!animated || !settled.current) {
      indicator.style.width = `${width}px`;
      indicator.style.transform = `translateX(${x}px)`;
      indicator.style.opacity = "1";
      settled.current = true;
      return;
    }

    // x と width は独立したスプリングとして動かす (Motion は個別 transform を
    // それぞれ別に補間する)。ひとつのスプリングで 2 次元の距離を扱うと、
    // 成分ごとに速度が違うときに同期がずれる。
    void animate(indicator, { x, width }, spring.move);
  };

  // 最新の moveIndicator を保持する。ResizeObserver の購読は張り替えたくないが、
  // コールバックの中で古い value を掴んでいると、リサイズ時に前の選択位置へ
  // インジケータが飛んでしまう。
  const moveRef = useRef(moveIndicator);
  moveRef.current = moveIndicator;

  useLayoutEffect(() => {
    moveIndicator(true);
    // moveIndicator は毎レンダー作り直されるので依存に入れない。
    // 選択が変わったときとセグメントが差し替わったときだけ動かせばよい。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, segments]);

  // フォントの読み込みや折り返し、文字サイズ設定の変更で各セグメントの幅が変わったら追従する。
  // レイアウトの都合で位置が変わるだけなのでアニメーションはさせない。
  useEffect(() => {
    const list = listRef.current;
    if (!list || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => moveRef.current(false));
    observer.observe(list);
    return () => observer.disconnect();
  }, []);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const delta = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
    if (delta === 0) return;
    e.preventDefault();
    const current = segments.findIndex((s) => s.value === value);
    if (current < 0) return;
    // 端は折り返す (radiogroup の一般的な挙動)
    const next = segments[(current + delta + segments.length) % segments.length];
    onChange(next.value);
    listRef.current
      ?.querySelector<HTMLElement>(`[data-value="${CSS.escape(next.value)}"]`)
      ?.focus();
  };

  return (
    <div
      className="segmented"
      role="radiogroup"
      aria-label={label}
      ref={listRef}
      onKeyDown={onKeyDown}
    >
      <span className="segmentedIndicator" ref={indicatorRef} aria-hidden="true" />
      {segments.map((segment) => {
        const selected = segment.value === value;
        return (
          <button
            key={segment.value}
            type="button"
            role="radio"
            data-value={segment.value}
            aria-checked={selected}
            // roving tabindex: Tab では選択中のものだけに入り、以降は左右キーで移動する
            tabIndex={selected ? 0 : -1}
            className="segmentedItem"
            // 押下の瞬間に選択を確定させる。touch-up を待たない。
            onPointerDown={() => onChange(segment.value)}
            // ポインタを持たない環境 (キーボード / 支援技術) 用のフォールバック。
            // onPointerDown 済みなら同じ値なので二重発火しても無害。
            onClick={() => onChange(segment.value)}
          >
            {segment.label}
          </button>
        );
      })}
    </div>
  );
}
